import { Close } from '@mui/icons-material';
import { Box, Button, Grid, Stack, TextField } from '@mui/material';
import { SxProps } from '@mui/system';
import React, { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import CAppPaper from '../../../../app/components/CAppPaper';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import langAuthOktaUserActivation from '../../../auth/features/okta-user-activation/langs/langAuthOktaUserActivation';
import useUserUpdatePassword from '../../hooks/useUserUpdatePassword';
import langUserProfile from '../../langs/langUserProfile';
import { FieldBox } from '../../pages/PUserProfile';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
type VUserProfilePasswordProps = {
	sxStyle: SxProps;
	sxEditButton: SxProps;
	sxTextField: SxProps;
	disableEdit: boolean;
};

interface IAuthPasswordUpdateForm {
	oldPassword: string;
	newPassword: string;
	newPasswordConfirmation: string;
}

const VUserProfilePassword = (props: VUserProfilePasswordProps) => {
	const { sxStyle, sxEditButton, sxTextField, disableEdit } = props;

	const { t, i18n } = useTranslation();

	const [editBlock, setEditBlock] = useState(false);

	const schemaPassword = yup
		.object({
			oldPassword: yup
				.string()
				.required(t(langAuthOktaUserActivation.field_error_password_required.tag))
				.min(8, t(langAuthOktaUserActivation.field_error_password_too_short.tag)),
			newPassword: yup
				.string()
				.required(t(langAuthOktaUserActivation.field_error_password_required.tag))
				.min(8, t(langAuthOktaUserActivation.field_error_password_too_short.tag))
				.matches(
					/(.*[a-z].*)/,
					t(langAuthOktaUserActivation.field_error_password_must_contain_one_lowercase.tag),
				)
				.matches(
					/(.*[A-Z].*)/,
					t(langAuthOktaUserActivation.field_error_password_must_contain_one_uppercase.tag),
				)
				.matches(/(.*\d.*)/, t(langAuthOktaUserActivation.field_error_password_must_contain_one_number.tag)),
			newPasswordConfirmation: yup
				.mixed()
				.test(
					'match',
					t(langAuthOktaUserActivation.field_error_passwords_do_not_match.tag),
					function (password) {
						return password === this.parent.newPassword;
					},
				)
				.required(t(langAuthOktaUserActivation.field_error_password_confirm_required.tag)),
		})
		.required();
	const { mutate: updatePassword } = useUserUpdatePassword();

	const {
		handleSubmit: handleSubmitPassword,
		control,
		setError,
		formState: { errors },
	} = useForm<IAuthPasswordUpdateForm>({
		resolver: yupResolver(schemaPassword),
		defaultValues: {
			oldPassword: '',
			newPassword: '',
			newPasswordConfirmation: '',
		},
	});

	const onSubmitPassword: SubmitHandler<IAuthPasswordUpdateForm> = (data) => {
		updatePassword(
			{
				oldPassword: data.oldPassword ?? '',
				newPassword: data.newPassword ?? '',
			},
			{
				onSuccess: () => {
					setEditBlock(!editBlock);
				},
				onError: (e: unknown) => {
					const errors = (
						e as { response?: { data?: { errors?: Array<{ field: string; tags: string[] }> } } }
					)?.response?.data?.errors;
					errors?.forEach((err) => {
						setError(err.field as keyof IAuthPasswordUpdateForm, {
							type: 'custom',
							message: i18n.exists(`user.profile.error_password_update_api_${err.tags[0]}`)
								? t(`user.profile.error_password_update_api_${err.tags[0]}`)
								: err.tags[0],
						});
					});
				},
			},
		);
	};

	const handleClickEditPassword = () => {
		setEditBlock(!editBlock);
	};

	return (
		<form onSubmit={handleSubmitPassword(onSubmitPassword)}>
			<CAppPaper sx={sxStyle} variant="outlined">
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Box className="title">
						<AppCustomIcon tag="user_password" className="icon" /> {t(langUserProfile.title_password.tag)}
					</Box>
					<Box>
						{editBlock ? (
							<>
								<Button
									variant="text"
									color="secondary"
									size="small"
									sx={{ ...sxEditButton, marginRight: '20px' }}
									type="button"
									startIcon={<Close />}
									onClick={() => handleClickEditPassword()}
								>
									{t(langUserProfile.btn_cancel.tag)}
								</Button>
								<Button
									variant="contained"
									color="secondary"
									size="small"
									sx={sxEditButton}
									type="submit"
									disabled={disableEdit}
									startIcon={<AppCustomIcon tag="save" className="icon" />}
								>
									{t(langUserProfile.btn_save.tag)}
								</Button>
							</>
						) : (
							<Button
								variant="text"
								color="secondary"
								size="small"
								type="button"
								sx={sxEditButton}
								disabled={disableEdit}
								startIcon={<AppCustomIcon tag="edit" className="icon" />}
								onClick={() => handleClickEditPassword()}
							>
								{t(langUserProfile.btn_edit.tag)}
							</Button>
						)}
					</Box>
				</Stack>
				{editBlock ? (
					<>
						<Grid container spacing={3} direction="row">
							<Grid item sm={editBlock ? 6 : 12} xs={12}>
								<FieldBox
									bEdit={editBlock}
									label={t(langUserProfile.field_current_password.tag)}
									value="*************"
									component={
										<Controller
											name="oldPassword"
											control={control}
											defaultValue=""
											render={({ field }) => (
												<TextField
													fullWidth
													{...field}
													error={!!errors?.oldPassword}
													helperText={errors?.oldPassword?.message ?? null}
													type="password"
													variant="outlined"
													sx={sxTextField}
												/>
											)}
										/>
									}
								/>
							</Grid>
						</Grid>
						<Grid container spacing={3} direction="row">
							<Grid item sm={6} xs={12} sx={{ display: editBlock ? '' : 'none' }}>
								<FieldBox
									bEdit={editBlock}
									label={t(langUserProfile.field_new_password.tag)}
									value="*************"
									component={
										<Controller
											name="newPassword"
											control={control}
											defaultValue=""
											render={({ field }) => (
												<TextField
													fullWidth
													{...field}
													error={!!errors?.newPassword}
													helperText={errors?.newPassword?.message ?? null}
													type="password"
													variant="outlined"
													sx={sxTextField}
												/>
											)}
										/>
									}
								/>
							</Grid>
							<Grid item sm={6} xs={12} sx={{ display: editBlock ? '' : 'none' }}>
								<FieldBox
									bEdit={editBlock}
									label={t(langUserProfile.field_confirm_password.tag)}
									value="*************"
									component={
										<Controller
											name="newPasswordConfirmation"
											control={control}
											defaultValue=""
											render={({ field }) => (
												<TextField
													fullWidth
													{...field}
													error={!!errors?.newPasswordConfirmation}
													helperText={errors?.newPasswordConfirmation?.message ?? null}
													type="password"
													variant="outlined"
													sx={sxTextField}
												/>
											)}
										/>
									}
								/>
							</Grid>
						</Grid>
					</>
				) : (
					<>
						<Box sx={{ marginTop: '20px' }}>
							<Trans t={t} i18nKey={langUserProfile.label_for_block_password.tag} />
						</Box>
					</>
				)}
			</CAppPaper>
		</form>
	);
};

export default VUserProfilePassword;
